import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PartyPopper, ShieldCheck, Smartphone, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Branded "welcome — claim your account" landing for lead customers created by manual orders/bookings.
export default function ClaimAccount() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { claimAccount } = useAuth();
  const token = sp.get("token") || "";
  const [info, setInfo] = useState(null); // {ok, name, email}
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    api.get(`/auth/claim-info?token=${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data)).catch(() => setInfo({ ok: false }))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setLoading(true);
    try {
      const res = await claimAccount(token, password);
      toast.success("Account claimed! Let's verify your mobile so you can track everything.");
      navigate(res?.needs_mobile_verification ? "/verify-mobile" : "/app/home");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not claim your account."); }
    finally { setLoading(false); }
  };

  const invalid = !token || (info && info.ok === false);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-sky-50 p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl" data-testid="claim-page">
        <Logo variant="lg" />
        {checking ? (
          <p className="mt-6 text-center text-sm text-slate-500">Checking your invite…</p>
        ) : invalid ? (
          <div className="mt-6 text-center" data-testid="claim-invalid">
            <p className="text-sm text-slate-500">This claim link is invalid or has expired. You can still request a fresh sign-in link.</p>
            <Link to="/forgot-password" className="mt-4 inline-block w-full rounded-xl brand-gradient-bg py-3 font-semibold text-white">Get a sign-in link</Link>
            <Link to="/login" className="mt-3 inline-block text-sm text-slate-500 hover:text-slate-700">Back to login</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6">
            <div className="flex items-center gap-2"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-100 text-sky-600"><PartyPopper className="h-5 w-5" /></span>
              <div>
                <h1 className="font-brand text-2xl font-bold text-slate-800">Welcome{info?.name ? `, ${info.name.split(" ")[0]}` : ""}!</h1>
                <p className="text-xs text-slate-500">Claim your Suffle account{info?.email ? ` · ${info.email}` : ""}</p>
              </div>
            </div>
            <p className="mt-4 rounded-xl bg-sky-50 p-3 text-sm text-slate-600">An order or booking was created for you. Set a password to claim your account — then you can track everything and get updates.</p>
            <label className="mt-5 block text-sm font-medium text-slate-600">Create a password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="claim-password"
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-sky-400" placeholder="At least 6 characters" />
            <label className="mt-4 block text-sm font-medium text-slate-600">Confirm password</label>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="claim-confirm"
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-sky-400" placeholder="Re-enter password" />
            <button type="submit" disabled={loading} data-testid="claim-submit"
              className="mt-6 w-full rounded-xl brand-gradient-bg py-3 font-semibold text-white disabled:opacity-50">
              {loading ? "Claiming…" : "Claim my account"}
            </button>
            <div className="mt-5 space-y-2 border-t border-slate-100 pt-4 text-xs text-slate-500">
              <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Your data is safe and already waiting for you.</p>
              <p className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-sky-500" /> Next: verify your mobile to track orders & rides live.</p>
              <p className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-500" /> Then explore everything Suffle has to offer.</p>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}
